"use client";

/**
 * Mock-interview card — the interview/hire closer persona's deep-help surface.
 * Generates a role-tailored set of likely interview questions, each with what
 * the interviewer is probing, a STAR-style model-answer outline, and red flags
 * to avoid. Backed by the free `generateMockInterview` action.
 */

import { useState } from "react";
import type { MockInterviewState } from "@/lib/match/types";

type Locale = "en" | "id";

function l(locale: Locale, en: string, id: string): string {
	return locale === "en" ? en : id;
}

type Props = {
	state: MockInterviewState;
	locale: Locale;
	pending: boolean;
	onStart: () => void;
};

export function MockInterviewCard({ state, locale, pending, onStart }: Props) {
	const [open, setOpen] = useState<number | null>(0);

	if (state.phase === "intro") {
		return (
			<div
				data-testid="mock-interview-card"
				data-mock-phase={state.phase}
				className="rounded-2xl border border-primary/20 bg-card p-4"
			>
				<p className="mb-1 text-sm font-semibold text-foreground">
					{l(locale, "Mock interview", "Simulasi wawancara")}
				</p>
				<p className="mb-3 text-sm text-muted-foreground">
					{l(
						locale,
						"I'll run a quick mock interview tailored to your target role — the questions you'll actually get asked, with how to nail each one.",
						"Saya buatkan simulasi wawancara sesuai target role kamu — pertanyaan yang benar-benar ditanya, plus cara menjawabnya.",
					)}
				</p>
				<button
					type="button"
					disabled={pending}
					onClick={onStart}
					className="inline-flex min-h-[44px] w-full cursor-pointer items-center justify-center rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{l(locale, "Start mock interview", "Mulai simulasi")}
				</button>
			</div>
		);
	}

	if (state.phase === "loading") {
		return (
			<div
				data-testid="mock-interview-card"
				data-mock-phase={state.phase}
				className="rounded-2xl border border-border bg-card p-4"
			>
				<p className="animate-pulse text-sm text-muted-foreground motion-reduce:animate-none">
					{l(
						locale,
						"Preparing your mock interview…",
						"Menyiapkan simulasi wawancara…",
					)}
				</p>
			</div>
		);
	}

	if (state.phase === "quota_exceeded") {
		return (
			<div
				data-testid="mock-interview-card"
				data-mock-phase={state.phase}
				className="space-y-2 rounded-2xl border border-border bg-card p-4"
			>
				<p className="text-sm text-muted-foreground">
					{l(
						locale,
						"You've used your free mock interviews this month.",
						"Sesi simulasi wawancara gratis bulan ini sudah habis.",
					)}
				</p>
			</div>
		);
	}

	if (state.phase === "error") {
		return (
			<div
				data-testid="mock-interview-card"
				data-mock-phase={state.phase}
				className="space-y-3 rounded-2xl border border-border bg-card p-4"
			>
				<p className="text-sm text-muted-foreground">
					{l(
						locale,
						"Couldn't prepare the interview just now. Want to try again?",
						"Belum bisa menyiapkan simulasi. Mau coba lagi?",
					)}
				</p>
				<button
					type="button"
					disabled={pending}
					onClick={onStart}
					className="inline-flex min-h-[44px] cursor-pointer items-center rounded-xl border border-border px-3 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
				>
					{l(locale, "Try again", "Coba lagi")}
				</button>
			</div>
		);
	}

	// phase === "ready"
	return (
		<div
			data-testid="mock-interview-card"
			data-mock-phase={state.phase}
			className="space-y-3 rounded-2xl border border-border bg-card p-4"
		>
			<p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
				{l(locale, "Mock interview", "Simulasi wawancara")}
				{state.role ? ` · ${state.role}` : ""}
			</p>

			<ol className="space-y-2">
				{state.questions.map((q, i) => {
					const isOpen = open === i;
					return (
						<li key={q.question} className="rounded-xl border border-border">
							<button
								type="button"
								onClick={() => setOpen(isOpen ? null : i)}
								className="flex w-full cursor-pointer items-start gap-2 px-3 py-2 text-left"
								aria-expanded={isOpen}
							>
								<span className="mt-0.5 text-xs font-bold text-primary">
									{i + 1}
								</span>
								<span className="flex-1 text-sm font-medium text-foreground">
									{q.question}
								</span>
							</button>
							{isOpen ? (
								<div className="space-y-2 border-border border-t px-3 py-2 text-xs text-muted-foreground">
									<p>
										<span className="font-semibold text-foreground">
											{l(locale, "What they're probing: ", "Yang dinilai: ")}
										</span>
										{q.why}
									</p>
									<p>
										<span className="font-semibold text-foreground">
											{l(
												locale,
												"Strong-answer outline: ",
												"Kerangka jawaban: ",
											)}
										</span>
										{q.modelAnswerOutline}
									</p>
									<p>
										<span className="font-semibold text-foreground">
											{l(locale, "Avoid: ", "Hindari: ")}
										</span>
										{q.redFlags}
									</p>
								</div>
							) : null}
						</li>
					);
				})}
			</ol>

			{state.focusAdvice ? (
				<p className="rounded-xl bg-muted/50 px-3 py-2 text-xs text-foreground">
					<span className="font-semibold">
						{l(locale, "Focus first: ", "Fokus dulu: ")}
					</span>
					{state.focusAdvice}
				</p>
			) : null}
		</div>
	);
}
